/**
 * Tool Agent — stateless natural language → tool call translator.
 *
 * Receives natural language, uses LLM to pick the right tool and extract params,
 * executes the tool, returns the result as a string.
 *
 * Manages two tool sources:
 * 1. Category tools (from ToolCategory tree) — static
 * 2. Worker tool peers (from personal workers) — dynamic, cached
 *
 * Also provides executeCode as a utility tool for computation/logic tasks.
 */

import { getAllToolDefinitions, executeTool as categoryExecuteTool, getTopLevelCategorySummary } from 'tool_sdk'
import { executeCode } from 'tool_sdk'

const TOOL_AGENT_PROMPT = `You are a tool-calling agent. Convert the user's natural language request into tool calls.

Rules:
1. Analyze the request, pick the best tool, extract parameters, and call it.
2. If a request needs multiple tools, call them all at once.
3. If the request is ambiguous, pick the most likely match.
4. If the user asks what tools/capabilities you have, call listAvailableTools.
5. Use executeCode only for computation, math, or logic tasks — not for calling other tools.
6. ONLY call tools from the provided tool list. NEVER invent or guess tool names.
7. If no tool matches, call listAvailableTools and let the user know what you can do.`

/**
 * Build definitions for a worker that implements getToolDefinitions RPC.
 */
function buildWorkerDefs(peer, defs) {
  const prefix = `worker_${peer.mentionName}`
  const definitions = []
  const dispatchMap = {}

  for (const def of defs) {
    const funcName = `${prefix}_${def.name}`
    definitions.push({
      name: funcName,
      description: `[${peer.displayName}] ${def.description}`,
      parameters: def.parameters,
    })
    dispatchMap[funcName] = { peerId: peer.roleId, toolName: def.name }
  }

  return { definitions, dispatchMap }
}

/**
 * Build fallback definition for a worker without getToolDefinitions.
 */
function buildFallbackWorkerDef(peer) {
  const funcName = `worker_${peer.mentionName}_call`
  return {
    definitions: [{
      name: funcName,
      description: `[${peer.displayName}] ${peer.description || 'Worker tool'}. Pass toolPath and toolArgs.`,
      parameters: {
        type: 'object',
        properties: {
          toolPath: { type: 'string', description: 'Tool method name to call' },
          toolArgs: { type: 'object', description: 'Arguments for the tool' },
        },
        required: ['toolPath'],
      },
    }],
    dispatchMap: { [funcName]: { peerId: peer.roleId, isFallback: true } },
  }
}

/**
 * Create a tool agent instance.
 *
 * @param {Object} options
 * @param {import('tool_sdk').ToolCategory} options.rootCategory - Assembled ToolCategory tree
 * @param {Function} options.llmCall - (messages, tools) => chatSingleStep response
 * @param {Function} options.buildCtx - () => ctx with auth info for tool execution
 * @param {Function} [options.getWorkerToolPeers] - () => peer[] of online tool-type workers
 * @param {Function} [options.roleCall] - (roleId, method, params) => result
 * @returns {{ getDescription: () => string, getToolList: () => Array, execute: (ctx, message) => Promise<string> }}
 */
export function createToolAgent({ rootCategory, llmCall, buildCtx, getWorkerToolPeers, roleCall }) {
  // Category tools — static, computed once
  const { definitions: categoryDefs, pathMap } = getAllToolDefinitions(rootCategory)

  // Built-in tool definitions
  const executeCodeDef = {
    name: 'executeCode',
    description: 'Execute JavaScript code for computation or math only (e.g. calculate 2+2). NOT for listing tools or answering questions.',
    parameters: {
      type: 'object',
      properties: {
        code: { type: 'string', description: 'JavaScript code to execute. Must return a value.' },
      },
      required: ['code'],
    },
  }
  const LIST_TOOLS_DEF = {
    name: 'listAvailableTools',
    description: 'Call this when the user asks what tools, capabilities, or features you have. Returns all available tools.',
    parameters: { type: 'object', properties: {} },
  }

  // Worker definitions cache: peerId → { definitions, dispatchMap }
  const workerDefCache = new Map()

  async function getWorkerDefs(peer) {
    if (workerDefCache.has(peer.roleId)) return workerDefCache.get(peer.roleId)
    let result
    try {
      const defs = await roleCall(peer.roleId, 'getToolDefinitions', [])
      result = Array.isArray(defs) && defs.length ? buildWorkerDefs(peer, defs) : buildFallbackWorkerDef(peer)
    } catch {
      result = buildFallbackWorkerDef(peer)
    }
    workerDefCache.set(peer.roleId, result)
    return result
  }

  function syncCache(currentPeers) {
    const currentIds = new Set(currentPeers.map(p => p.roleId))
    for (const id of workerDefCache.keys()) {
      if (!currentIds.has(id)) workerDefCache.delete(id)
    }
  }

  async function collectDefinitions() {
    if (!getWorkerToolPeers || !roleCall) {
      return { allDefinitions: categoryDefs, dispatchMap: {} }
    }

    const peers = getWorkerToolPeers()
    syncCache(peers)

    if (!peers.length) {
      return { allDefinitions: categoryDefs, dispatchMap: {} }
    }

    const allDefinitions = [...categoryDefs]
    const dispatchMap = {}
    const workerResults = await Promise.all(peers.map(p => getWorkerDefs(p)))
    for (const wr of workerResults) {
      allDefinitions.push(...wr.definitions)
      Object.assign(dispatchMap, wr.dispatchMap)
    }

    return { allDefinitions, dispatchMap }
  }

  function getDescription() {
    const categories = getTopLevelCategorySummary(rootCategory)
    const list = categories.map(c => `${c.id}(${c.description})`).join(', ')
    let desc = `Natural language tool agent. Supported: ${list}.`
    if (getWorkerToolPeers) {
      const peers = getWorkerToolPeers()
      if (peers.length) {
        desc += ` Workers: ${peers.map(p => p.displayName).join(', ')}.`
      }
    }
    desc += ' Describe what you need in plain language.'
    return desc
  }

  function buildToolListResponse() {
    return getToolList().map(t => `- ${t.name}: ${t.description}`).join('\n')
  }

  async function execute(_, message) {
    const { allDefinitions, dispatchMap } = await collectDefinitions()
    const toolDefs = [LIST_TOOLS_DEF, executeCodeDef, ...allDefinitions]

    const messages = [
      { role: 'system', content: TOOL_AGENT_PROMPT },
      { role: 'user', content: message },
    ]
    console.log('[Tool Agent] messages:', JSON.stringify(messages, null, 2))
    console.log('[Tool Agent] toolDefs:', toolDefs.map(t => t.name))

    const response = await llmCall(messages, toolDefs)
    console.log('[Tool Agent] response:', JSON.stringify(response, null, 2))

    if (!response.toolCalls?.length) {
      return 'I am a tool-calling agent. Tell me what you need in plain language and I will find the right tool. You can ask "what tools do you have?" to see my capabilities.'
    }

    const ctx = buildCtx ? await buildCtx() : {}

    const results = await Promise.all(response.toolCalls.map(async (tc) => {
      const funcName = tc.function.name
      let args
      try {
        args = typeof tc.function.arguments === 'string'
          ? JSON.parse(tc.function.arguments)
          : tc.function.arguments || {}
      } catch {
        return { tool: funcName, error: 'Invalid tool arguments' }
      }

      // Built-in: list tools
      if (funcName === 'listAvailableTools') {
        return { tool: funcName, result: buildToolListResponse() }
      }

      // Built-in: execute code (for computation/logic)
      if (funcName === 'executeCode') {
        const result = await executeCode(ctx, args)
        return { tool: funcName, ...result }
      }

      // Category tool
      if (pathMap[funcName]) {
        const result = await categoryExecuteTool(rootCategory, ctx, { toolPath: pathMap[funcName], toolArgs: args })
        return { tool: funcName, ...result }
      }

      // Worker tool
      if (dispatchMap[funcName]) {
        const { peerId, toolName, isFallback } = dispatchMap[funcName]
        try {
          const method = isFallback ? args.toolPath : toolName
          const methodArgs = isFallback ? (args.toolArgs || {}) : args
          const result = await roleCall(peerId, method, [methodArgs])
          return { tool: funcName, success: true, result }
        } catch (err) {
          return { tool: funcName, success: false, error: err.message }
        }
      }

      return { tool: funcName, error: `Unknown tool: ${funcName}` }
    }))

    return JSON.stringify({ results })
  }

  /**
   * Get structured tool list for UI display (categories + worker peers).
   * Synchronous — no RPC calls, uses cached peer list.
   * @returns {Array<{ name: string, description: string, type: 'category'|'worker' }>}
   */
  function getToolList() {
    const tools = getTopLevelCategorySummary(rootCategory).map(c => ({
      name: c.id,
      description: c.description,
      type: 'category',
    }))
    if (getWorkerToolPeers) {
      for (const peer of getWorkerToolPeers()) {
        tools.push({ name: peer.displayName, description: peer.description || '', type: 'worker' })
      }
    }
    return tools
  }

  function getSystemPrompt() { return TOOL_AGENT_PROMPT }

  return { getDescription, getToolList, getSystemPrompt, execute }
}
